import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Calendar, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBooking() {
  const { calendarId } = useParams();
  const [cal, setCal] = useState(null);
  const [error, setError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { axios.get(`${API}/public/calendar/${calendarId}`).then(({ data }) => setCal(data)).catch(() => setError("Calendar not available.")); }, [calendarId]);

  const loadSlots = useCallback(() => {
    setTime(null);
    axios.get(`${API}/public/calendar/${calendarId}/slots?date=${date}`).then(({ data }) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [calendarId, date]);
  useEffect(() => { if (cal) loadSlots(); }, [cal, loadSlots]);

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/calendar/${calendarId}/book`, { date, time, ...form, answers });
      setDone(data.appointment);
      toast.success("Booked!");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not book"); }
    finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#090D16] text-white/60">{error}</div>;
  if (!cal) return <div className="min-h-screen flex items-center justify-center bg-[#090D16]"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>;
  const brand = cal.brand?.color || "#6366F1";

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-[#090D16] text-white px-5">
      <div className="text-center max-w-md" data-testid="booking-confirmed">
        <CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: brand }} />
        <h1 className="text-3xl font-display font-bold">You're booked!</h1>
        <p className="text-white/60 mt-3">{new Date(done.start).toLocaleString()} (UTC)</p>
        <p className="text-white/40 text-sm mt-2">A confirmation has been recorded. See you then.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#090D16] text-white">
      <div className="max-w-lg mx-auto px-5 py-16" data-testid="public-booking-page">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40">{cal.brand?.name}</p>
        <h1 className="text-3xl font-display font-bold mt-2 flex items-center gap-2"><Calendar className="w-6 h-6" style={{ color: brand }} />{cal.name}</h1>
        <p className="text-white/50 text-sm mt-1">{cal.slot_minutes} minute session</p>

        <div className="mt-8 space-y-5">
          <div>
            <label className="text-sm text-white/70">Choose a date</label>
            <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)}
              className="mt-1.5 w-full h-11 px-4 rounded-lg bg-white/5 border border-white/15 text-white outline-none" data-testid="booking-date" />
          </div>
          <div>
            <label className="text-sm text-white/70 flex items-center gap-1.5"><Clock className="w-4 h-4" />Available times (UTC)</label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {slots.length === 0 && <p className="col-span-4 text-white/40 text-sm py-4">No times available this day.</p>}
              {slots.map((s) => (
                <button key={s} onClick={() => setTime(s)} data-testid={`slot-${s}`}
                  className={`h-10 rounded-lg text-sm font-medium border transition-colors ${time === s ? "text-white border-transparent" : "border-white/15 text-white/80 hover:border-white/40"}`}
                  style={time === s ? { background: brand } : {}}>{s}</button>
              ))}
            </div>
          </div>

          {time && (
            <div className="space-y-3 border-t border-white/10 pt-5">
              {["name", "email", "phone"].map((k) => (
                <input key={k} type={k === "email" ? "email" : "text"} placeholder={k[0].toUpperCase() + k.slice(1) + (k === "phone" ? " (optional)" : "")}
                  value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`booking-${k}`}
                  className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/15 text-white placeholder:text-white/40 outline-none" />
              ))}
              {(cal.custom_questions || []).map((q) => (
                <input key={q.key} placeholder={q.label + (q.required ? " *" : "")} required={q.required}
                  value={answers[q.key] || ""} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })} data-testid={`booking-q-${q.key}`}
                  className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/15 text-white placeholder:text-white/40 outline-none" />
              ))}
              <button onClick={book} disabled={busy || !form.name || !form.email} data-testid="confirm-booking"
                className="w-full h-12 rounded-full font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: brand }}>
                {busy ? "Booking…" : `Confirm ${time}`}
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-white/30 text-xs pt-10">Powered by {cal.brand?.name || "Momento"}</p>
      </div>
    </div>
  );
}
